import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import { MdTrendingUp } from 'react-icons/md';
import { FaHeart } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getCustomerCrossSell, toggleCustomerLikedProduct } from 'interfaces/customer';

interface CrossSellSuggestion {
    productId: string;
    productName: string;
    productRef?: string;
    orderCount: number;
}

export default function CrossSellSuggestions(props: { customerId: string }) {
    const { customerId } = props;
    const [suggestions, setSuggestions] = useState<CrossSellSuggestion[]>([]);
    const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);

    const fetchSuggestions = async () => {
        setLoading(true);
        const res = await getCustomerCrossSell(customerId);
        setSuggestions(Array.isArray(res) ? res : []);
        setLoading(false);
    };

    useEffect(() => {
        fetchSuggestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const handleAddToFavorites = async (productId: string) => {
        const res = await toggleCustomerLikedProduct(customerId, productId);
        if (res) {
            setAddedIds((prev) => new Set(prev).add(productId));
            toast.success('Produit ajouté aux favoris du client.');
        } else {
            toast.error("Erreur lors de l'ajout aux favoris.");
        }
    };

    return (
        <Card extra="w-full !p-4 border col-span-12">
            <div className="flex items-center gap-2 mb-3">
                <MdTrendingUp className="text-emerald-500" />
                <h4 className="text-lg font-medium text-navy-700 dark:text-white">
                    Produits complémentaires suggérés
                </h4>
            </div>

            {loading ? (
                <p className="text-sm text-gray-400">Chargement...</p>
            ) : suggestions.length === 0 ? (
                <p className="text-sm text-gray-500 italic">
                    Pas assez de données pour suggérer des produits complémentaires pour ce client.
                </p>
            ) : (
                <div className="flex flex-col gap-2">
                    {suggestions.map((product) => (
                        <div
                            key={product.productId}
                            className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5"
                        >
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span className="text-xs font-medium text-emerald-800 truncate">
                                    {product.productName}
                                </span>
                                {product.productRef && (
                                    <span className="text-xs text-emerald-500 flex-shrink-0">
                                        ({product.productRef})
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs text-emerald-700 font-semibold">
                                    Commandé par {product.orderCount} autre
                                    {product.orderCount > 1 ? 's' : ''} client
                                    {product.orderCount > 1 ? 's' : ''}
                                </span>
                                <button
                                    onClick={() => handleAddToFavorites(product.productId)}
                                    disabled={addedIds.has(product.productId)}
                                    title="Ajouter aux favoris du client"
                                    className={`transition-colors ${
                                        addedIds.has(product.productId)
                                            ? 'text-pink-300 cursor-default'
                                            : 'text-pink-400 hover:text-pink-600'
                                    }`}
                                >
                                    <FaHeart size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}
